import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    IntAdmPermissions,
    UserActionConfig,
    UserActionEnum,
    DealUserAccess,
    UserHelperService,
    UserRecord,
} from '../shared';
import { PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortable, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { AccessTypes, AuthService } from '../../core/services/auth.service';
import { UserAvailableColumns } from './user-table.column.types';
import { Router } from '@angular/router';
import { ConfigurationService } from '../../core/services/configuration.service';

@Component({
    selector: 'app-user-table',
    templateUrl: './user-table.component.html',
    styleUrls: ['./user-table.component.scss'],
})
export class UserTableComponent implements AfterViewInit, OnChanges {
    @Input() userRecords: UserRecord[];
    @Input() dealUserAccessRecords: DealUserAccess[];
    @Input() searchText: string;
    @Input() initialSort: MatSortable;
    @Input() page: number;
    @Input() pageSize: number;
    @Input() totalRecords: number = 0;
    @Input() showCreateUser: boolean;
    @Input() fetching: boolean;
    @Input() set displayedColumns(value: string[]) {
        if (value && value.length > 0) {
            this._displayedColumns = value;
        } else {
            this._displayedColumns = this.defaultColumns;
        }
    }

    @Output() pageEvent = new EventEmitter<PageEvent>();
    @Output() sortEvent = new EventEmitter<Sort>();
    @Output() userToEditEvent = new EventEmitter<UserRecord>();
    @Output() userActionNav = new EventEmitter<UserActionConfig>();

    @ViewChild(MatSort) sort: MatSort;

    public dataSource: MatTableDataSource<UserRecord>;
    public defaultColumns: string[] = [
        UserAvailableColumns.NsContactName,
        UserAvailableColumns.Email,
        UserAvailableColumns.FirstName,
        UserAvailableColumns.MiddleName,
        UserAvailableColumns.LastName,
        UserAvailableColumns.Status,
        UserAvailableColumns.Products,
        UserAvailableColumns.Actions,
    ];
    public get displayedColumns(): string[] {
        return this._displayedColumns;
    }
    public iconWasClicked = false;
    public selectedIndex = null;
    public permissionBlockUser = IntAdmPermissions.userBlock;
    public permissionUnblockUser = IntAdmPermissions.userUnblock;
    public permissionEditUser = IntAdmPermissions.userEdit;
    public permissionDeactivateUser = IntAdmPermissions.userDeactivate;
    public permissionSendPwResetUser = IntAdmPermissions.userSendPwReset;
    public permissionResendInvUser = IntAdmPermissions.userResendInv;

    private _displayedColumns: string[];
    private auth0StatusTooltipCreated = 'The user has been created, but has not yet been invited.';
    private auth0StatusTooltipInvited = 'The user has been invited, but has not completed account setup.';
    private auth0StatusTooltipActive =
        'The user has set up password and MFA methods, and can access available SRSA products.';
    private auth0StatusTooltipBlocked =
        'The user is prevented from accessing SRSA applications. Unblock the user to restore access.';
    private auth0StatusTooltipDeactivated =
        'The user has been removed, and cannot access SRSA applications. Deactivated users cannot be reactivated.';

    constructor(
        private userHelperService: UserHelperService,
        private authService: AuthService,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private configurationService: ConfigurationService,
    ) {
        // Assign the data to the data source for the table to render
        this.dataSource = new MatTableDataSource(this.userRecords);
    }

    ngAfterViewInit() {
        if (this.sort) {
            if (this.initialSort.id === 'firstName') {
                this.initialSort.id = 'user';
            }
            this.sort.sort({ ...this.initialSort, disableClear: false });
            this.dataSource.sort = this.sort;
            this.cdr.detectChanges();
        }
    }

    ngOnChanges(_: SimpleChanges): void {
        this.dataSource = new MatTableDataSource(this.userRecords);
    }

    handlePageEvent(e: PageEvent) {
        this.pageEvent.emit(e);
    }

    handleSortChange(s: Sort) {
        if (s.active === 'user') {
            s.active = 'firstName';
        }
        this.sortEvent.emit(s);
    }

    handleUserSelected(u: UserRecord) {
        this.router.navigate(['/user-access/users'], {
            queryParams: { userId: u.id, cUserId: u.nsId, tabSelectedSingleUser: 0 },
        });
    }

    public openMenu($event: MouseEvent, index: number) {
        this.iconWasClicked = true;
        this.selectedIndex = index;
        $event?.stopPropagation();
        $event?.preventDefault();
    }

    public handleMenuClosedEvent($event, index: number) {
        this.iconWasClicked = false;
        this.selectedIndex = -1;
        $event?.stopPropagation();
        $event?.preventDefault();
    }

    public hideMenu() {
        this.iconWasClicked = false;
        this.selectedIndex = -1;
    }

    public canEditUser(status): boolean {
        const canEditUser = this.userHelperService.canEditUser(status);
        const perm = this.authService.checkAccess(this.permissionEditUser) === AccessTypes.FULL;
        return canEditUser && perm;
    }

    public canViewUserActivity(status): boolean {
        return !!this.userHelperService.canViewUserActivity(status);
    }
    //
    // public canResendInvitation(status): boolean {
    //     const canResendInvitation = !!this.userHelperService.canResendInvitation(status);
    //     const perm = this.authService.checkAccess(this.permissionResendInvUser) === AccessTypes.FULL;
    //     return canResendInvitation && perm;
    // }

    public handleEditUserClick(element) {
        this.userToEditEvent.emit(element);
        this.hideMenu();
    }

    public handleUserActivityClick(index) {
        this.hideMenu();

        this.userActionNav.emit({
            arrayIndex: index,
            action: UserActionEnum.activity,
        });
    }
    //
    // public handleResendInvitation(index) {
    //     this.hideMenu();
    //     this.userActionNav.emit({
    //         arrayIndex: index,
    //         action: UserActionEnum.resendInv,
    //     });
    // }

    public getLoginDisplayStatus(userRecord: UserRecord): string {
        let displayValue = 'Created';

        // Only if the user exists in User Admin, signified by having an id.
        if (userRecord.id && userRecord.auth0Id) {
            switch (userRecord?.status?.toLowerCase()) {
                case 'active':
                case 'existing': {
                    displayValue = 'Active';
                    break;
                }
                case 'invited': {
                    displayValue = 'Invited';
                    break;
                }
                case 'blocked': {
                    displayValue = 'Blocked';
                    break;
                }
                case 'deactivated': {
                    displayValue = 'Deactivated';
                    break;
                }
            }
        }

        return displayValue;
    }

    public getLoginStatusTooltip(userRecord: UserRecord): string {
        let displayValue = this.auth0StatusTooltipCreated;

        // Only if the user exists in User Admin, signified by having an id.
        if (userRecord.id && userRecord.auth0Id) {
            switch (userRecord?.status?.toLowerCase()) {
                case 'active':
                case 'existing': {
                    displayValue = this.auth0StatusTooltipActive;
                    break;
                }
                case 'invited': {
                    displayValue = this.auth0StatusTooltipInvited;
                    break;
                }
                case 'blocked': {
                    displayValue = this.auth0StatusTooltipBlocked;
                    break;
                }
                case 'deactivated': {
                    displayValue = this.auth0StatusTooltipDeactivated;
                    break;
                }
            }
        }

        return displayValue;
    }

    public handleImpersonateUser(id) {
        const url = `${this.configurationService.envConfig.getValue().sharpUrl}/?userId=${id}`;
        window.open(url, '_blank');
    }
}
